import { scaleBand, scaleTime } from '@visx/scale';
import { useMemo } from 'react';
import { QUALITY_TIMELINE } from '../../consts';
import { VisibleTimeRange } from '../../lib/visible-time-range';
import { TimelineChartXScale, TimelineChartYScale } from '../lib/timeline-chart-scale-types';

export type TimelineChartGeometry = {
  topMargin: number;
  height: number;
  innerWidth: number;
  innerHeight: number;
  xScale: TimelineChartXScale;
  yScale: TimelineChartYScale;
  visibleStart: Date;
  visibleEnd: Date;
};

type UseTimelineChartGeometryArgs = {
  width: number;
  includeTopAxis: boolean;
  stationIds: string[];
  visibleRange: VisibleTimeRange;
  globalStart: Date;
  globalEnd: Date;
};

/**
 * Computes chart dimensions and x/y scales; also determines whether the chart should render at all.
 */
export function useTimelineChartGeometry({
  width,
  includeTopAxis,
  stationIds,
  visibleRange,
  globalStart,
  globalEnd,
}: UseTimelineChartGeometryArgs): { isValid: boolean; geometry: TimelineChartGeometry | null } {
  const visibleStart = visibleRange.from;
  const visibleEnd = visibleRange.to;

  const topMargin = includeTopAxis ? QUALITY_TIMELINE.margin.top : 0;
  const height =
    stationIds.length * QUALITY_TIMELINE.rowHeight + topMargin + QUALITY_TIMELINE.margin.bottom;
  const innerWidth = width - QUALITY_TIMELINE.margin.left - QUALITY_TIMELINE.margin.right;
  const innerHeight = height - topMargin - QUALITY_TIMELINE.margin.bottom;

  const { xScale, yScale } = useMemo(() => {
    const x = scaleTime<number>({
      domain: [visibleStart, visibleEnd],
      range: [0, innerWidth],
    });
    const y = scaleBand<string>({
      domain: stationIds,
      range: [0, innerHeight],
      padding: 0,
    });
    return { xScale: x, yScale: y };
  }, [visibleStart, visibleEnd, innerWidth, innerHeight, stationIds]);

  const isValid = !(
    width < 10 ||
    stationIds.length === 0 ||
    Number.isNaN(globalStart.getTime()) ||
    Number.isNaN(globalEnd.getTime()) ||
    Number.isNaN(visibleStart.getTime()) ||
    Number.isNaN(visibleEnd.getTime()) ||
    !(visibleEnd.getTime() > visibleStart.getTime())
  );

  const geometry: TimelineChartGeometry | null = isValid
    ? {
        topMargin,
        height,
        innerWidth,
        innerHeight,
        xScale,
        yScale,
        visibleStart,
        visibleEnd,
      }
    : null;

  return { isValid, geometry };
}
